import { useParams, useSearchParams } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EntityHistory, useCanSeeHistory } from '@/features/audit-log';
import { ApiError } from '@/shared/api/api-error';
import { ErrorState } from '@/shared/components/error-state';
import { NotFoundPage } from '@/shared/components/errors/not-found-page';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { StatusBadge } from '@/shared/components/status-badge';

import { useCountry } from '../api/pricing.queries';
import {
  ChannelsPanel,
  ChannelsTabLabel,
  FxPanel,
  FxTabLabel,
  PreviewPanel,
} from '../components/country-panels';
import { HealthPanel } from '../components/catalogue-health-panel';
import { CountrySettingsPanel } from '../components/country-settings-panel';
import { PricingParametersPanel } from '../components/pricing-parameters-panel';

const TABS = ['pricing', 'preview', 'channels', 'fx', 'health', 'settings', 'history'] as const;
type Tab = (typeof TABS)[number];

/**
 * One country: pricing parameters (live and draft), a price
 * preview, payment channels, exchange rates, catalogue health, and its settings
 * with the readiness checklist and activation. The open
 * tab lives in the URL (`?tab=`), so a view can be shared.
 */
export function CountryPage() {
  const { countryId } = useParams<{ countryId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const parsedId = Number(countryId);
  const isValidId = Number.isInteger(parsedId) && parsedId > 0;
  const country = useCountry(isValidId ? parsedId : undefined);
  const canSeeHistory = useCanSeeHistory('Country');

  const requested = searchParams.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(requested ?? '')
    ? (requested as Tab)
    : 'pricing';

  if (!isValidId) return <NotFoundPage />;
  if (country.isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-7 w-52" />
        <Skeleton className="h-64" />
      </PageContainer>
    );
  }
  if (
    country.error instanceof ApiError &&
    (country.error.isNotFound || country.error.isForbidden)
  ) {
    return <NotFoundPage />;
  }
  if (country.error || !country.data) {
    return (
      <PageContainer>
        <ErrorState error={country.error} onRetry={() => void country.refetch()} />
      </PageContainer>
    );
  }

  const data = country.data;

  return (
    <PageContainer>
      <PageHeader
        backTo={{ to: '/countries', label: 'Countries' }}
        title={
          <span className="flex items-center gap-2.5">
            {data.nameEn}
            <StatusBadge variant={data.isActive ? 'success' : 'neutral'}>
              {data.isActive ? 'Active' : 'Inactive'}
            </StatusBadge>
          </span>
        }
        description={`Prices in ${data.currencyCode ?? '—'} · settles in ${data.settlementCurrency ?? '—'} · VAT ${data.vat}% · verification by ${data.verificationMethod ?? '—'}`}
      />

      <Card>
        <CardContent className="pt-4">
          <Tabs
            value={tab}
            onValueChange={(value) => {
              const next = new URLSearchParams(searchParams);
              next.set('tab', value);
              setSearchParams(next, { replace: true });
            }}
          >
            <TabsList>
              <TabsTrigger value="pricing">Pricing parameters</TabsTrigger>
              <TabsTrigger value="preview">Price preview</TabsTrigger>
              <TabsTrigger value="channels">
                <ChannelsTabLabel countryId={data.id} />
              </TabsTrigger>
              <TabsTrigger value="fx">
                <FxTabLabel countryId={data.id} />
              </TabsTrigger>
              <TabsTrigger value="health">Catalogue health</TabsTrigger>
              <TabsTrigger value="settings">Settings &amp; readiness</TabsTrigger>
              {canSeeHistory && <TabsTrigger value="history">History</TabsTrigger>}
            </TabsList>
            <TabsContent value="pricing" className="pt-3">
              <PricingParametersPanel countryId={data.id} />
            </TabsContent>
            <TabsContent value="preview" className="pt-3">
              <PreviewPanel countryId={data.id} hasDraft={data.hasPricingDraft} />
            </TabsContent>
            <TabsContent value="channels" className="pt-3">
              <ChannelsPanel countryId={data.id} countryName={data.nameEn ?? 'this country'} />
            </TabsContent>
            <TabsContent value="fx" className="pt-3">
              <FxPanel countryId={data.id} />
            </TabsContent>
            <TabsContent value="health" className="pt-3">
              <HealthPanel countryId={data.id} />
            </TabsContent>
            <TabsContent value="settings" className="pt-3">
              <CountrySettingsPanel country={data} />
            </TabsContent>
            {canSeeHistory && (
              <TabsContent value="history" className="pt-3">
                <EntityHistory entityType="Country" entityId={String(data.id)} />
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
